import { type IFavoriteProfessional } from '~/domain/FavoriteProfessionals';
import { type IEligibleCandidateEntity } from '~/domain/PermanentJobs';
import { type IRecentProfessional } from '~/domain/RecentProfessionals';
import { getRecentProfessionalId } from '~/domain/RecentProfessionals/recentProfessionalsHelpers';
import { type IRecommendedProfessional } from '~/domain/RecommendedProfessionals';
import { type IBadge } from '~/domain/RecommendedProfessionals/recommendedProfessionalsTypes';
import { type TEducationMap } from '~/domain/FavoriteProfessionals/favoriteProfessionalsTypes';
import { UserProfession } from '~/enums/UserProfession';

export type TProCategory = 'favorite' | 'recent' | 'new';

export interface IListItemData {
  id: string;
  fullName: string;
  isAvailable: boolean;
  isFavorite: boolean;
  profileImage: string;
  profession: string;
  professions: string[];
  rating?: {
    averageRating: number;
    professionalSkills?: number;
    interpersonalSkills?: number;
    timeManagement?: number;
  };
  badges: IBadge[];
  education: TEducationMap;
  category: TProCategory;
  professionCode: string;
}

type TProfessionLookup = (code: string) => string;

const resolveProfessionName = (
  professions: string[] | undefined,
  selectedProfession: string | undefined,
  fallback: string | undefined,
  getProfessionName: TProfessionLookup,
): { name: string; code: string } => {
  if (selectedProfession && professions?.includes(selectedProfession)) {
    return { name: getProfessionName(selectedProfession), code: selectedProfession };
  }
  const code = professions?.[0] ?? fallback ?? '';
  if (!code) return { name: '', code: '' };
  return { name: getProfessionName(code), code };
};

// The domain rating breakdowns all expose the same four averages; map them to the
// row's rating shape in one place so every `from*` adapter stays in sync.
interface IRatingBreakdown {
  averageRating: number;
  averageProfessionalSkills: number;
  averageInterpersonalSkills: number;
  averageTimeManagement: number;
}

const toListItemRating = (rating?: IRatingBreakdown): IListItemData['rating'] =>
  rating
    ? {
        averageRating: rating.averageRating,
        professionalSkills: rating.averageProfessionalSkills,
        interpersonalSkills: rating.averageInterpersonalSkills,
        timeManagement: rating.averageTimeManagement,
      }
    : undefined;

export const fromFavorite = (
  pro: IFavoriteProfessional,
  selectedProfession: string | undefined,
  getProfessionName: TProfessionLookup,
): IListItemData => {
  const { name, code } = resolveProfessionName(
    pro.professions,
    selectedProfession,
    undefined,
    getProfessionName,
  );
  return {
    id: pro.id,
    fullName: pro.fullName,
    isAvailable: pro.isAvailable ?? true,
    isFavorite: true,
    profileImage: pro.profileImage,
    profession: name,
    professions: pro.professions ?? [],
    rating: toListItemRating(pro.averageProfessionalRating),
    badges: pro.badges ?? [],
    education: pro.education ?? {},
    category: 'favorite',
    professionCode: code,
  };
};

export const fromRecent = (
  pro: IRecentProfessional,
  selectedProfession: string | undefined,
  getProfessionName: TProfessionLookup,
): IListItemData => {
  const { name, code } = resolveProfessionName(
    pro.professional?.professions,
    selectedProfession,
    pro.profession,
    getProfessionName,
  );
  return {
    id: getRecentProfessionalId(pro),
    fullName: pro.professional?.fullName ?? '',
    isAvailable: pro.isAvailable ?? true,
    isFavorite: false,
    profileImage: pro.professional?.image ?? '',
    profession: name || UserProfession.getName(pro.profession) || '',
    professions: pro.professional?.professions ?? [],
    rating: toListItemRating(pro.professional?.overallProfessionalRating),
    badges: pro.professional?.badges ?? [],
    education: pro.professional?.education ?? {},
    category: 'recent',
    professionCode: code || pro.profession || '',
  };
};

export const fromRecommended = (
  pro: IRecommendedProfessional,
  selectedProfession: string | undefined,
  getProfessionName: TProfessionLookup,
): IListItemData => {
  const { name, code } = resolveProfessionName(
    pro.professions,
    selectedProfession,
    undefined,
    getProfessionName,
  );
  return {
    id: pro.id,
    fullName: pro.fullName,
    isAvailable: pro.isAvailable ?? true,
    isFavorite: false,
    profileImage: pro.profileImage,
    profession: name,
    professions: pro.professions ?? [],
    rating:
      pro.rating != null
        ? {
            averageRating: pro.rating,
            professionalSkills: pro.averageProfessionalSkills,
            interpersonalSkills: pro.averageInterpersonalSkills,
            timeManagement: pro.averageTimeManagement,
          }
        : undefined,
    badges: pro.badges ?? [],
    education: pro.education ?? {},
    category: 'new',
    professionCode: code,
  };
};

/**
 * Maps a perm eligible candidate (from `useGetEligibleCandidates`) to the shared
 * invite-row shape so the Interview Details list can reuse the same
 * `EmbeddedListItem`/`TabContent` row as the temp invite flow.
 *
 * Every eligible candidate is invitable by construction: the backend already
 * removes genuine conflicts from the response and accepts invites regardless of
 * `hasMarkedAvailable` (that field is a sort hint — "ticked this date" — not a
 * permission). So `isAvailable`, which gates row selection, is always `true`
 * here; unlike the temp adapters where it reflects a real account-standing gate.
 */
export const fromEligibleCandidate = (candidate: IEligibleCandidateEntity): IListItemData => {
  const { name, code } = resolveProfessionName(
    candidate.profession,
    undefined,
    undefined,
    (professionCode) => UserProfession.getName(professionCode) ?? '',
  );
  return {
    id: candidate.id,
    fullName: candidate.fullName,
    isAvailable: true,
    isFavorite: false,
    profileImage: candidate.profileImage,
    profession: name,
    professions: candidate.profession ?? [],
    rating: toListItemRating(candidate.overallProfessionalRating),
    badges: candidate.badges ?? [],
    education: candidate.education ?? {},
    category: 'new',
    professionCode: code,
  };
};
